import React, { useEffect, useState } from 'react';
import { councilPortal } from '../services/api';

const STATUS_COLORS = {
  paid:    'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
};

const CATEGORY_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-yellow-500', 'bg-red-500',
];

export default function CouncilFinancials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    councilPortal.financials()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  if (!data) return <p className="text-gray-500">Failed to load financials.</p>;

  const maxAmount = Math.max(...(data.by_category?.map((c) => c.amount) || [1]));

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Financials</h1>
        <p className="text-gray-500 text-sm mt-1">
          {data.year} year-to-date expenditures — read-only view.
        </p>
      </div>

      {/* YTD total */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
        <p className="text-sm font-medium text-emerald-700">Total YTD Spend ({data.year})</p>
        <p className="text-4xl font-bold text-emerald-900 mt-1">
          ${Number(data.total_spend).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
        </p>
      </div>

      {/* By category */}
      {data.by_category?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Spend by Category</h2>
          <div className="space-y-3">
            {data.by_category.map((c, i) => (
              <div key={c.category}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 font-medium">{c.category}</span>
                  <span className="text-gray-900 font-semibold">
                    ${Number(c.amount).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${CATEGORY_COLORS[i % CATEGORY_COLORS.length]}`}
                    style={{ width: `${(c.amount / maxAmount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">All Invoices ({data.year})</h2>
        </div>
        {data.invoices?.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Flag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.invoices.map((inv) => (
                <tr key={inv.id} className={`hover:bg-gray-50 ${inv.anomaly_flag ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{inv.vendor || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{inv.category || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {inv.date ? new Date(inv.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    ${Number(inv.amount).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inv.anomaly_flag && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                        ⚠ Review
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-400 text-sm text-center py-10">No invoices found for {data.year}.</p>
        )}
      </div>
    </div>
  );
}
